import { ModalFuncProps } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { App, Button, Col, Form, Input, Modal, Row, Select, Space, Spin, Tooltip } from 'antd';

import * as actions from '../store/actions';

import { isGuid, renderNoDataOrSpinner } from '../../../shared/utils';

import { Store } from 'antd/es/form/interface';
import { isArray, isBoolean } from 'lodash';
import { AppDispatch, RootState } from '../../../store';
import { SaveRingCentralAccountRequest } from '../models';

export type RingCentralAccountSettingsModalProps = {
  showModal: boolean;
  onCloseModal: () => void;
  resetAndRefresh: () => void;
};

const RingCentralAccountSettingsModal = ({ showModal, onCloseModal, resetAndRefresh }: RingCentralAccountSettingsModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const didMount = useRef(false);

  const { modal } = App.useApp();

  const [formRef] = Form.useForm<SaveRingCentralAccountRequest>();

  const [isModified, setIsModified] = useState<boolean>(false);
  const [initiatingDone, setInitiatingDone] = useState<boolean>(false);

  const {
    ringCentralAccountSettings,
    ringCentralAccountSettingsLoading,
    saveRingCentralAccountSettingsLoading,
    allRingCentralPhones,
    allRingCentralPhonesLoading,
  } = useSelector((state: RootState) => state.ringCentral);
  const { messageControlParametersLoading } = useSelector((state: RootState) => state.smses);

  useEffect(() => {
    if (!didMount.current) {
      dispatch(actions.getRingCentralAccountSettings()).then(() => {
        setInitiatingDone(true);
        onReset();
      });

      didMount.current = true;
    }
  }, []);

  useEffect(() => {
    onReset();
  }, [ringCentralAccountSettings, ringCentralAccountSettingsLoading]);

  const onClose = (isModifiedModal: boolean) => {
    if (isModifiedModal) {
      modal.confirm({
        title: 'All changes will be lost. Are you sure you want to close?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          onReset();
          onCloseModal?.();
        },
      });
    } else {
      onReset();
      resetAndRefresh?.();
      onCloseModal?.();
    }
  };

  const onFinish = () => {
    formRef.validateFields().then((formData) => {
      dispatch(actions.saveRingCentralAccountSettings(formData)).then(() => {
        dispatch(actions.getMessageControlParameters()).then(() => {
          modal.success({
            title: 'Your settings are saved successfully',
            onOk: () => {
              setIsModified(false);
              onClose(false);
            },
          } as ModalFuncProps);
        });
      });
    });
  };

  const onRemove = () => {
    if (!ringCentralAccountSettings) {
      return;
    }

    modal.confirm({
      title: 'This is remove the current Ring Central Account settings. Are you sure you want to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        return new Promise((resolve, reject) => {
          dispatch(
            actions.removeRingCentralAccount({
              ringCentralAccountId: ringCentralAccountSettings.id,
              userIsLoggedIntoRingCentral: ringCentralAccountSettings?.userIsLoggedIntoRingCentral === true,
            }),
          )
            .then(() => {
              return dispatch(actions.getMessageControlParameters()).then(() => {
                modal.success({
                  title: 'Your settings are saved successfully',
                  onOk: () => {
                    setIsModified(false);
                    onClose(false);
                    resolve(undefined);
                  },
                });
              });
            })
            .catch(reject);
        });
      },
    });
  };

  const onReset = () => {
    formRef.resetFields();
    dispatch(actions.setAllRingCentralPhones([]));
    setIsModified(false);
  };

  const onPhonesSelectionOpen = (open: boolean) => {
    if (
      open === true &&
      isBoolean(ringCentralAccountSettings?.userIsLoggedIntoRingCentral) &&
      ringCentralAccountSettings?.userIsLoggedIntoRingCentral === true
    ) {
      dispatch(actions.getAllRingCentralPhones());
    }
  };

  const onLoginClick = () => {
    setInitiatingDone(false);

    if (ringCentralAccountSettings?.authorizeUri) {
      window.location.href = ringCentralAccountSettings?.authorizeUri;
    }
  };

  return (
    <Modal
      title="Ring Central account settings"
      open={showModal}
      footer={null}
      onCancel={() => onClose(isModified)}
      onClose={() => onClose(isModified)}
      width={600}
    >
      <div className="account-modal-settings">
        <Spin spinning={ringCentralAccountSettingsLoading || !initiatingDone}>
          <Form
            form={formRef}
            size="small"
            className="ring-central-account-form"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            initialValues={ringCentralAccountSettings as Store}
            onFinish={onFinish}
            onChange={() => (isModified ? null : setIsModified(true))}
          >
            <Form.Item hidden name="id">
              <Input type="hidden" />
            </Form.Item>

            {ringCentralAccountSettings?.userIsLoggedIntoRingCentral !== true ? (
              <>
                <Button type="primary" onClick={onLoginClick} className="login-redirect">
                  Ring Central login page
                </Button>

                <br />

                <p>
                  To continue enjoying seamless SMS service, we need you to log in to your Ring Central account in our system. If you've already done
                  so, it's possible your token has expired - reconnecting is quick and easy!
                </p>
              </>
            ) : null}

            <Form.Item label="Phones" name="phoneNumber" rules={[{ type: 'string', required: true, max: 128 }]}>
              <Select
                placeholder="Select a phone"
                notFoundContent={renderNoDataOrSpinner(allRingCentralPhonesLoading)}
                onDropdownVisibleChange={onPhonesSelectionOpen}
                disabled={
                  !isBoolean(ringCentralAccountSettings?.userIsLoggedIntoRingCentral) ||
                  ringCentralAccountSettings?.userIsLoggedIntoRingCentral !== true
                }
              >
                {isArray(allRingCentralPhones)
                  ? allRingCentralPhones.map((phone) => (
                      <Select.Option key={phone} value={phone}>
                        {phone}
                      </Select.Option>
                    ))
                  : null}
              </Select>
            </Form.Item>

            <Row>
              <Col span={24} className="modal-form-control-buttons">
                <Space className="control-buttons-space">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={saveRingCentralAccountSettingsLoading || messageControlParametersLoading}
                    disabled={
                      !isBoolean(ringCentralAccountSettings?.userIsLoggedIntoRingCentral) ||
                      ringCentralAccountSettings?.userIsLoggedIntoRingCentral !== true
                    }
                  >
                    {isGuid(ringCentralAccountSettings?.id) ? 'Update' : 'Save'}
                  </Button>

                  <Tooltip title="Reset all changes to the unchanged state">
                    <Button
                      htmlType="button"
                      onClick={onReset}
                      disabled={
                        !isBoolean(ringCentralAccountSettings?.userIsLoggedIntoRingCentral) ||
                        ringCentralAccountSettings?.userIsLoggedIntoRingCentral !== true
                      }
                    >
                      Reset
                    </Button>
                  </Tooltip>

                  {isGuid(ringCentralAccountSettings?.id) || ringCentralAccountSettings?.userIsLoggedIntoRingCentral === true ? (
                    <Button
                      type="primary"
                      danger
                      loading={saveRingCentralAccountSettingsLoading || messageControlParametersLoading}
                      onClick={onRemove}
                    >
                      Remove
                    </Button>
                  ) : undefined}

                  <Button onClick={() => onClose(isModified)}>Cancel</Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Spin>
      </div>
    </Modal>
  );
};

export default RingCentralAccountSettingsModal;
